<template>
  <v-dialog
      v-model="dialog"
      hide-overlay
      content-class="minimized-user-dialog"
  >
    <v-card elevation="0">
      <v-col class="text-right px-4">
        <v-btn icon @click="closeDialog">
          <v-icon large color="primary">close</v-icon>
        </v-btn>
      </v-col>

      <v-card-text>
        <v-form ref="form">
          <div class="pa-5">
            <h3 class="font-weight-bold">{{ $t('components:minimized-users-form.title') }}</h3>

            <!-- Name -->
            <DuplicateCheckField
                ref="duplName"
                :value="users.name"
                :label="$t('common:name')"
                item-additional-info-field="email"
                required
                item-text="name"
                @input="users.name = $event"
                @checkForDuplicates="checkByName"
                @clickedShowItem="showUser"
            />

            <!-- Phone -->
            <v-text-field
                outlined
                v-model="users.phone"
                :label="$t('components:minimized-users-form.labels.phone')"
                :rules="[ruleRequired($t('utils:rules.required'))]"
            />

            <!-- Email -->
            <DuplicateCheckField
                ref="duplEmail"
                :value="users.email"
                :label="$t('common:email')"
                item-additional-info-field="email"
                required
                item-text="name"
                @input="users.email = $event"
                @checkForDuplicates="checkByEmail"
                @clickedShowItem="showUser"
            />

            <!-- Role -->
            <v-select
                :items="roles"
                item-value="value"
                item-text="text"
                v-model="users.bookHierarchy"
                :menu-props="{ bottom: true, offsetY: true }"
                outlined
                :label="$t('common:role')"
                :rules="[ruleRequired($t('utils:rules.required'))]"
            />

            <CustomerTypeahead
                v-if="isAdmin"
                ref="userCustomer"
                :value="users.customer"
                @input="users.customer = $event"
                :label="$t('components:minimized-users-form.labels.customer-typeahead')"
            />

            <v-checkbox
                v-if="profileCustomer && !isAdmin"
                v-model="isSameAsProfileCustomer"
            >
              <template v-slot:label>
                <span>{{ $t("components:minimized-users-form.labels.customer") }} <b>{{ profileCustomer.name }}</b></span>
              </template>
            </v-checkbox>

            <BaseButton
                color="primary"
                :btn-text="$t('buttons:save')"
                @clickedButton="saveUser()"
            />

            <BaseButton
                color="grey"
                :btn-text="$t('buttons:back')"
                @clickedButton="closeDialog()"
            />
          </div>
        </v-form>
      </v-card-text>
    </v-card>
  </v-dialog>
</template>
<script>
import UsersService from '@/modules/core/services/users-service';
import CustomerService from "@/modules/core/services/customer-service";
import RoleAuthService from "@/modules/core/services/auth/role-auth-service";

import Utils from "@/modules/core/services/utils";
import DataUtils from "@/modules/core/services/data-utils";
import {ruleRequired} from "@/modules/core/services/rules";

import Users from "@/modules/core/data/users";
import Customer from "@/modules/core/data/customer"

import BaseButton from "@/modules/core/components/Base/BaseButton";
import CustomerTypeahead from "@/modules/core/components/CustomerTypeAhead";
import {mapGetters} from "vuex";
import DuplicateCheckField from "@/modules/core/components/DuplicateCheckField";
import DuplicateCheckService from "@/modules/core/services/duplicate-check-service";

export default {
  name: 'MinimizedUsersDialogForm',
  components: {
    DuplicateCheckField,
    CustomerTypeahead,
    BaseButton
  },
  data() {
    return {
      users: new Users(),
      edit: false,
      roles: [],

      dialog: false,
      profileCustomer: new Customer(),
      isSameAsProfileCustomer: false
    }
  },
  computed: {
    currentLang() {
      return this.$store.state.translation.language;
    },
    ...mapGetters('auth', ['isAdmin']),
  },
  methods: {
    closeDialog() {
      this.dialog = false;
      this.$refs.form.reset();
    },

    openDialogWithName(name) {
      this.dialog = true;
      this.users.name = name;
    },

    saveUser() {
      if (!this.$refs.form.validate()) {
        return;
      }

      if (!this.isAdmin && this.isSameAsProfileCustomer) {
        this.users.customer = this.profileCustomer.id;
      } else if (!this.isAdmin && !this.isSameAsProfileCustomer) {
        this.users.customer = null;
      }

      var searchRoleText = this.roles.find(role => role.value === this.users.bookHierarchy);

      UsersService.registerUser(this.users, searchRoleText.text,
          (res) => {
            Utils.showSuccessSnackbar(this.$t("components:minimized-users-form.snackbar.register-success", { mail: this.users.email}));
            this.closeDialog();
            this.$emit('onRegistered', res.data)
          },
          (serviceName, error) => {
            if (error.response.headers.error_cause && this.$t('error-causes:' + error.response.headers.error_cause)) {
              Utils.showFailureSnackbar(this.$t("error-causes:" + error.response.headers.error_cause));
            } else {
              Utils.showFailureSnackbar(this.$t("components:minimized-users-form.snackbar.register-failure", { mail: this.users.email}));
            }
          }
      );
    },

    checkByEmail(term) {
      DuplicateCheckService.checkUsers('EMAIL', term,
          (res) => {
            this.$refs.duplEmail.fillData(res.data);
          },
          (serviceName, error) => {
            this.handleDuplicateErrorCause(serviceName, error);
          }
      )
    },

    checkByName(term) {
      DuplicateCheckService.checkUsers('NAME', term,
          (res) => {
            this.$refs.duplName.fillData(res.data);
          },
          (serviceName, error) => {
            this.handleDuplicateErrorCause(serviceName, error);
          }
      )
    },

    showUser(item) {
      this.$router.push(`/${this.$store.state.manager.settingsModuleUrl}/profile/customer/users?user=${item.id}`)
    },

    handleDuplicateErrorCause(serviceName, error) {
      if (error.response.headers.error_cause && this.$t('error-causes:' + error.response.headers.error_cause)) {
        Utils.showWarningSnackbar(this.$t("error-causes:" + error.response.headers.error_cause));
      } else {
        this.$logger.error(serviceName + error);
      }
    },

    ruleRequired
  },
  watch: {
    currentLang() {
      Utils.sleep(500).then(() => {
        for (var i = 0; i < this.roles.length; i++) {
          this.roles[i].text = this.$t('enums:hierarchy.' + this.roles[i].value);
        }
        this.$refs.form.resetValidation()
      })
    },

    dialog() {
      if (this.dialog) {
        RoleAuthService.readAuthorities(
            (resp) => {
              resp.data.forEach((object) => {
                let role = new Object();
                role.value = object;
                role.text = this.$t('enums:hierarchy.' + object);
                this.roles.push(role);
              });

              //load and set the default customer data to the customer fields
              CustomerService.readProfileFromAPI(
                  (res) => {
                    this.profileCustomer = DataUtils.parseDataToSingleObject(res.data, Customer);

                    if (this.isAdmin && res.data) {
                      this.users.customer = res.data.id;
                      this.$refs.userCustomer.initialise(res.data.id)
                    }
                  },
                  (error) => {
                    Utils.showWarningSnackbar(this.$t('snackbar:load-failure', {name: this.$t('components:minimized-users-form.snackbar.customer-data')}))
                    this.$logger.error(error);
                  }
              )
            },
            (error) => {
              this.$logger.info(error);
              Utils.showFailureSnackbar(this.$t('snackbar:load-failure', {name: this.$t('common:role_plural')}))
            }
        )
      }
    }
  },
}
</script>
<style lang="scss">
.v-dialog__content.v-dialog__content--active {
  z-index: 500 !important;
}

.minimized-user-dialog {
  width: auto!important;
}
</style>
